import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchDeck } from "../lib/api";
import { SlideRenderer } from "./Slides";
import { ChevronLeft, ChevronRight, Printer, Pencil } from "lucide-react";

export default function DeckEngine() {
  const [deck, setDeck] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    fetchDeck().then(setDeck).catch((e) => console.error("deck load failed", e));
  }, []);

  const total = deck?.slides?.length || 0;

  const go = useCallback(
    (dir) => setCurrent((c) => Math.min(Math.max(c + dir, 0), total - 1)),
    [total]
  );
  const goTo = useCallback((i) => setCurrent(Math.min(Math.max(i, 0), total - 1)), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(-1); }
      else if (e.key === "Home") { goTo(0); }
      else if (e.key === "End") { goTo(total - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, goTo, total]);

  if (!deck) {
    return (
      <div className="flex h-screen items-center justify-center bg-paper">
        <div className="mono animate-pulse text-xs uppercase tracking-[0.3em] text-stone">
          Loading the signal…
        </div>
      </div>
    );
  }

  const slides = deck.slides || [];
  const activeSlide = slides[current];
  const isDark = activeSlide?.type === "ask";

  return (
    <div className="deck-root relative h-screen w-screen overflow-hidden bg-paper grain">
      {/* Top bar */}
      <header className="no-print pointer-events-none absolute inset-x-0 top-0 z-30 flex items-start justify-between p-5 md:p-7">
        <div className="pointer-events-auto flex items-center gap-3">
          <img
            src={deck.images?.logoBadge}
            alt="The Canary Report"
            className="h-11 w-11 flex-shrink-0 rounded-full md:h-12 md:w-12"
          />
          <div>
            <div className={`font-display text-lg font-bold leading-none tracking-tight md:text-xl ${isDark ? "text-paper" : ""}`}>
              The Canary Report
            </div>
            <div className={`overline mt-1 ${isDark ? "text-paper/70" : "text-stone"}`}>Cooperative Development Brief</div>
          </div>
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            data-testid="export-pdf-button"
            onClick={() => window.print()}
            className="group flex items-center gap-2 border border-ink bg-surface px-3 py-2 text-xs font-medium transition-colors hover:bg-canary"
          >
            <Printer className="h-3.5 w-3.5" />
            <span className="mono uppercase tracking-widest">Export PDF</span>
          </button>
          <Link
            to="/admin"
            data-testid="admin-link"
            className="flex items-center gap-2 border border-ink bg-ink px-3 py-2 text-xs font-medium text-paper transition-colors hover:bg-night"
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="mono uppercase tracking-widest">Edit</span>
          </Link>
        </div>
      </header>

      {/* Vertical dot nav */}
      <nav
        data-testid="deck-dot-nav"
        className="no-print absolute right-5 top-1/2 z-30 hidden -translate-y-1/2 flex-col items-center gap-2.5 md:flex"
        aria-label="Slide navigation"
      >
        {slides.map((s, i) => (
          <button
            key={s.key || i}
            data-testid={`dot-nav-${i}`}
            onClick={() => goTo(i)}
            title={s.nav}
            aria-label={`Go to ${s.nav}`}
            className="group flex items-center gap-2"
          >
            <span className={`mono hidden text-[0.6rem] uppercase tracking-widest text-stone opacity-0 transition-opacity group-hover:opacity-100 lg:inline ${i === current ? "opacity-100 text-ink" : ""}`}>
              {s.nav}
            </span>
            <span
              className={`block transition-all duration-300 ${
                i === current ? "h-3 w-3 bg-canary ring-1 ring-ink" : "h-1.5 w-1.5 bg-ink/40 group-hover:bg-ink"
              }`}
            />
          </button>
        ))}
      </nav>

      {/* Stage */}
      <main className="deck-stage" data-testid="deck-engine">
        {slides.map((s, i) => (
          <section
            key={s.key || i}
            className="deck-slide"
            data-active={i === current}
            data-testid={`slide-${s.key || i}`}
            aria-hidden={i === current ? "false" : "true"}
          >
            <img
              src={deck.images?.logoBadge}
              alt="The Canary Report"
              className="pointer-events-none absolute right-8 top-8 z-20 hidden h-16 w-16 rounded-full print:block"
            />
            <SlideRenderer slide={s} images={deck.images || {}} active={i === current} />
          </section>
        ))}
      </main>

      {/* Bottom bar */}
      <footer className="no-print absolute inset-x-0 bottom-0 z-30 p-5 md:p-7">
        {/* progress */}
        <div className="mb-4 h-[3px] w-full bg-ink/10">
          <div
            className="h-full bg-canary transition-all duration-500"
            style={{ width: `${((current + 1) / total) * 100}%` }}
          />
        </div>
        <div className="flex items-end justify-between">
          <div>
            <div className={`overline ${isDark ? "text-paper/70" : "text-stone"}`}>{activeSlide?.nav}</div>
            <div className={`font-display mt-1 text-2xl leading-none tracking-tight md:text-3xl ${isDark ? "text-paper" : ""}`}>
              <span data-testid="slide-counter">
                {String(current + 1).padStart(2, "0")}
              </span>
              <span className={isDark ? "text-paper/60" : "text-stone"}> / {String(total).padStart(2, "0")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="prev-button"
              onClick={() => go(-1)}
              disabled={current === 0}
              className="flex h-11 w-11 items-center justify-center border border-ink bg-surface transition-all hover:bg-ink hover:text-paper disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="next-button"
              onClick={() => go(1)}
              disabled={current === total - 1}
              className="flex h-11 items-center gap-2 border border-ink bg-ink px-5 text-paper transition-all hover:bg-night disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Next slide"
            >
              <span className="mono text-xs uppercase tracking-widest">Next</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
